import { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, CircularProgress, Button, Stack, useMediaQuery, useTheme, Collapse, Grid, IconButton, Tooltip, Snackbar, Alert, Checkbox, Card, CardContent, Avatar, TextField, InputAdornment, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, RadioGroup, Radio, FormControlLabel, Popover } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import { Chip } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { VscNewFile } from 'react-icons/vsc';
import { FaUser } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import DescriptionIcon from '@mui/icons-material/Description';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import { formatRows } from '../../utils/formatRows';
import NoDataComponent from '../../components/NoDataComponent';
import CustomNoDataOverlay from '../../components/CustomNoDataOverlay';
import CenterLoader from '../../components/CenterLoader';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import LoadingOverlay from '../../components/LoadingOverlay';
import SkeletonLoader from '../../components/SkeletonLoader';
import { motion } from 'framer-motion';

const getFinancialYearRange = (now = new Date()) => {
    const month = now.getMonth();
    const startYear = month >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    const from = `${startYear}-04-01`;
    const to = `${startYear + 1}-03-31`;
    return { from, to };
};

const normalizeDate = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split("-").reverse().join("/") : v;

// Avatar helpers
function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = ['#e74c3c', '#3498db', '#2ecc71', '#e67e22', '#9b59b6', '#1abc9c', '#3f51b5', '#e91e63', '#795548', '#00bcd4',];
    return colors[Math.abs(hash) % colors.length];
}

function getInitials(name) {
    if (!name) return 'I';
    return name.trim().charAt(0).toUpperCase();
}

export default function ProformaInvoice_v2({ connectionString, pdfString }) {
    const PDF_API_BASE = pdfString;
    const { user, selectedCompany } = useAuth();
    const API_URL = `${connectionString}/api`;
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const isSmallDevice = useMediaQuery(theme.breakpoints.down(500));

    const [saleOrderRows, setSaleOrderRows] = useState([]);
    const [saleOrderLoading, setSaleOrderLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [proformaRows, setProformaRows] = useState([]);
    const [proformaLoading, setProformaLoading] = useState(false);
    const [proformaSelectionModel, setProformaSelectionModel] = useState([]);
    const [selectedSaleOrderRows, setSelectedSaleOrderRows] = useState([]);
    const [selectedSaleOrderIds, setSelectedSaleOrderIds] = useState([]);
    const [filterText, setFilterText] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [filterPopupMode, setFilterPopupMode] = useState('datewise');
    const [filterPopupFromDate, setFilterPopupFromDate] = useState('');
    const [filterPopupToDate, setFilterPopupToDate] = useState('');
    const [filterPopupFirms, setFilterPopupFirms] = useState([]);
    const [firmOptions, setFirmOptions] = useState([]);
    const [firmAllOptions, setFirmAllOptions] = useState([]);
    const [firmInputValue, setFirmInputValue] = useState('');
    const [firmLoading, setFirmLoading] = useState(false);
    const [filterPopupSnapshot, setFilterPopupSnapshot] = useState(null);
    const [filterPopupSeries, setFilterPopupSeries] = useState([]);
    const [seriesOptions, setSeriesOptions] = useState([]);
    const [seriesLoading, setSeriesLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [loadingPdfId, setLoadingPdfId] = useState(null);
    const [pdfOverlayVisible, setPdfOverlayVisible] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [pdfUri, setPdfUri] = useState(null);
    const [pdfError, setPdfError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exportExcelEnabled, setExportExcelEnabled] = useState(false);
    const [masterReportData, setMasterReportData] = useState([]);
    const [detailsReportData, setDetailsReportData] = useState([]);
    const [reportLoading, setReportLoading] = useState(false);

    const [pdfMenuAnchorEl, setPdfMenuAnchorEl] = useState(null);
    const [pdfInvoiceIdForCopy, setPdfInvoiceIdForCopy] = useState(null);
    const [selectedCopyType, setSelectedCopyType] = useState('Original');
    const [customCopyText, setCustomCopyText] = useState('');

    const userId = user?.UserId || null;
    const companyId = selectedCompany?.CompId || null;
    const role = user?.UserCategory || null;
    const params = { userId, userCategory: role, companyId };

    const [openProformaDeleteDialog, setOpenProformaDeleteDialog] = useState(false);
    const [pendingProformaDeleteIds, setPendingProformaDeleteIds] = useState([]);


    // =============== SALE ORDER COLUMNS (for DataGrid) - Previously Challan Columns ===============
    const saleOrderColumns = [
        {
            field: 'select', headerName: '', width: 40, sortable: false, disableColumnMenu: true, filterable: false,
            renderHeader: () => {
                const referencePartyId = selectedSaleOrderRows.length > 0 ? String(selectedSaleOrderRows[0].PartyID ?? '') : (saleOrderRows[0] ? String(saleOrderRows[0].PartyID ?? '') : null);
                const eligibleRows = referencePartyId ? saleOrderRows.filter(row => String(row.PartyID ?? '') === referencePartyId) : [];
                const allSelected = eligibleRows.length > 0 && eligibleRows.every(row => selectedSaleOrderIds.includes(Number(row.SOId)));
                const someSelected = selectedSaleOrderIds.length > 0 && !allSelected;

                return (
                    <Checkbox checked={allSelected} indeterminate={someSelected} disabled={!referencePartyId}
                        onChange={(e) => {
                            if (e.target.checked) {
                                if (!referencePartyId) return;
                                const newSelectedRows = eligibleRows.slice();
                                setSelectedSaleOrderRows(newSelectedRows);
                                setSelectedSaleOrderIds(newSelectedRows.map(r => Number(r.SOId)));
                            } else {
                                setSelectedSaleOrderRows([]);
                                setSelectedSaleOrderIds([]);
                            }
                        }}
                    />
                );
            },
            renderCell: ({ row }) => {
                const rowIdNum = Number(row.SOId);
                const checked = selectedSaleOrderIds.includes(rowIdNum);
                return (
                    <Checkbox checked={checked}
                        onChange={(e) => {
                            const checkedNow = e.target.checked;
                            setSelectedSaleOrderRows(prevRows => {
                                const prevPartyId = prevRows.length > 0 ? String(prevRows[0].PartyID ?? '') : null;
                                if (checkedNow) {
                                    const rowPartyId = String(row.PartyID ?? '');
                                    if (prevPartyId && rowPartyId !== prevPartyId) {
                                        setSnackbar({ open: true, message: 'Oops! Client name mismatch - select only sale orders of same client.', severity: 'warning' });
                                        return prevRows;
                                    }
                                    const already = prevRows.some(r => String(r.SOId) === String(row.SOId));
                                    if (already) return prevRows;
                                    const newRows = [...prevRows, row];
                                    setSelectedSaleOrderIds(newRows.map(r => Number(r.SOId)));
                                    return newRows;
                                } else {
                                    const newRows = prevRows.filter(r => Number(r.SOId) !== rowIdNum);
                                    setSelectedSaleOrderIds(newRows.map(r => Number(r.SOId)));
                                    return newRows;
                                }
                            });
                        }}
                    />
                );
            },
        },
        { field: 'SODate', headerName: 'SO Date', flex: 0.8, filterable: true, renderCell: (p) => <Typography variant="body2">{p.value || '-'}</Typography> },
        { field: 'SONumber', headerName: 'SO Number', flex: 1.2 },
        { field: 'RefNo', headerName: 'Ref No.', flex: 1 },
        {
            field: 'clientname', headerName: 'FirmName', flex: 2.5,
            renderCell: (params) => (
                <Box sx={{ cursor: 'pointer', whiteSpace: 'normal', wordBreak: 'break-word', my: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>{params.row.clientname || '-'}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FaUser color="#FF9800" />
                        <Typography variant="body2">{params.row.contactPerson || '-'}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">📲 {params.row.mobileNo || '-'}</Typography>
                </Box>
            ),
        },
        {
            field: 'location', headerName: 'Location', flex: 1.5, filterable: true,
            valueGetter: (value, row) => [row.city, row.state].filter(Boolean).join(', '),
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">{params.row.city || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">{params.row.state || '-'}</Typography>
                </Box>
            ),
        },
        ...(user?.UserCategory !== "SalesMan" ? [{
            field: "BillAmt", headerName: "Total Amt", flex: 1, align: "right", headerAlign: "right",
            renderCell: (params) => {
                const num = Number(params.row.BillAmt);

                if (!Number.isFinite(num)) return "-";

                return `₹${num.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })}`;
            },
        }] : []),
    ];

    // =============== PROFORMA INVOICE COLUMNS (for DataGrid) - Previously Invoice Columns ===============
    const baseProformaColumns = [
        { field: "PIDate", headerName: "Bill Date", flex: 0.6, filterable: true, },
        {
            field: "PINumber", headerName: "PI No", flex: 0.6, align: "center", headerAlign: "center",
            renderCell: (params) => (
                <Typography variant="body2" sx={{ color: "blue", textDecoration: "underline", cursor: "pointer", textTransform: 'uppercase' }} onClick={() => navigate(`/main/ProformaInvoiceMaster-edit/${params.row.id}`)} > {params.value ?? "-"} </Typography>
            ),
        },
        {
            field: "companyName", headerName: "FirmName", flex: 1.5,
            renderCell: (params) => (
                <Box sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    <Typography variant="subtitle2" sx={{ fontSize: "0.85rem" }}>{params.row.companyName || "-"}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <FaUser color="#FF9800" />
                        <Typography variant="body2">{params.row.ContactPerson || "-"}</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">📲 {params.row.mobileNo || "-"}</Typography>
                </Box>
            ),
        },
        {
            field: "city", headerName: "Location", flex: 0.5, align: "right", headerAlign: "right", filterable: true,
            valueGetter: (value, row) => [row.city, row.state].filter(Boolean).join(", "),
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" fontWeight={600}>{params.row.city || "N/A"}</Typography>
                    <Typography variant="body2" color="text.secondary">{params.row.state}</Typography>
                </Box>
            ),
        },
        {
            field: "BillAmt",
            headerName: "Bill Amount",
            flex: 0.8,
            align: "right",
            headerAlign: "right",
            filterable: true,
            type: "number",
            valueGetter: (value) => (value === null || value === undefined || value === "" ? null : Number(value)),
            renderCell: (params) => {
                const num = Number(params.row.BillAmt);
                if (!Number.isFinite(num)) return "-";
                return `₹${num.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                })}`;
            },
        },
        {
            field: "actions", headerName: "Action", flex: 0.3, sortable: false, filterable: false, align: "right", headerAlign: "right",
            renderCell: (params) => {
                const thisId = params.row.id;
                if (loadingPdfId && Number(loadingPdfId) === Number(thisId)) {
                    return <CircularProgress size={20} />;
                }
                return (
                    <IconButton color="primary" onClick={(e) => { handlePdfMenuOpen(e, params.row.id); }}>
                        <PrintIcon />
                    </IconButton>
                );
            },
        },
    ];

    const proformaColumns = useMemo(() => {
        let cols = [...baseProformaColumns];
        if (role !== 'User') {
            // Checkbox column
            cols.unshift({
                field: 'select', headerName: '', width: 48, sortable: false, disableColumnMenu: true, filterable: false,
                renderHeader: () => {
                    const allIds = proformaRows.map(r => Number(r.id));
                    const allSelected = allIds.length > 0 && allIds.every(id => proformaSelectionModel.includes(id));
                    const someSelected = proformaSelectionModel.length > 0 && !allSelected;
                    return (
                        <Checkbox checked={allSelected} indeterminate={someSelected}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setProformaSelectionModel(allIds);
                                } else {
                                    setProformaSelectionModel([]);
                                }
                            }}
                            disabled={proformaRows.length === 0}
                        />
                    );
                },
                renderCell: (params) => {
                    const idNum = Number(params.row.id);
                    const checked = proformaSelectionModel.includes(idNum);
                    return (
                        <Checkbox checked={checked} size="small" onChange={(e) => { e.stopPropagation(); setProformaSelectionModel(prev => prev.includes(idNum) ? prev.filter(i => i !== idNum) : [...prev, idNum]); }} />
                    );
                },
            });

            // Delete column
            cols.push({
                field: 'delete', headerName: '', flex: 0.2, align: 'right', headerAlign: 'right', sortable: false, filterable: false,
                renderCell: (params) => {
                    const idNum = Number(params.row.id);
                    const isSelected = proformaSelectionModel.includes(idNum);
                    return (
                        <Tooltip title={isSelected ? 'Delete selected proforma invoices' : 'Select row to enable delete'}>
                            <span>
                                <IconButton color="error" size="small" disabled={!isSelected} onClick={(e) => { e.stopPropagation(); handleProformaDelete(proformaSelectionModel); }} > <DeleteIcon /> </IconButton>
                            </span>
                        </Tooltip>
                    );
                },
            });
        }
        return cols;
    }, [proformaRows, proformaSelectionModel, role, loadingPdfId]);

    // =============== DATA FETCHING ===============
    const handleCardClick = async () => {
        if (!expanded) {
            setSaleOrderLoading(true);
            try {
                // Changed API endpoint to pending-proformainvoice
                const res = await axios.get(`${API_URL}/pending-proformainvoice`, { params });
                const data = Array.isArray(res.data) ? res.data : [];
                console.log(data);
                const rows = data.map((row, idx) => ({
                    id: row.SOId ?? idx,
                    SOId: row.SOId,
                    SONumber: row.SONumber,
                    RefNo: row.RefNo,
                    SODate: new Date(row.SODate).toLocaleDateString('en-GB'),
                    clientname: row.FirmName,
                    contactPerson: row.ContactPerson,
                    mobileNo: row.MobileNo,
                    city: row.City,
                    state: row.state,
                    BillAmt: Number(row.BillAmt) || 0,
                    PartyID: row.PartyID,
                }));
                setSaleOrderRows(rows);
            } catch (err) {
                console.log('Error fetching pending sale orders:', err);
            } finally {
                setSaleOrderLoading(false);
            }
        }
        setExpanded(prev => !prev);
    };

    const fetchProformaData = useCallback(async () => {
        setProformaLoading(true);
        try {
            // Changed API endpoint to get-proforma-invoice
            const response = await axios.get(`${API_URL}/get-proforma-invoice`, { params });
            const data = response.data;
            console.log(data)
            const rows = (Array.isArray(data) ? data : []).map(r => ({
                id: r.PImastId,
                PIDate: r.PIDate ? new Date(r.PIDate).toLocaleDateString('en-GB') : "-",
                PINumber: r.PINumber?.toUpperCase() || r.PINumber,
                RefNo: r.RefNo || '—',
                companyName: (r.clientNameFromMaster || "").trim(),
                ContactPerson: r.clientContactPerson,
                mobileNo: r.clientMobileNo,
                city: r.clientCity,
                state: r.clientState,
                BookTypeId: r.BookTypeId,
                BillAmt: Number(r.BillAmt) || 0,
            }));
            setProformaRows(rows);
        } catch (err) {
            console.error("Error fetching proforma invoices:", err);
        } finally {
            setProformaLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProformaData();
    }, [fetchProformaData]);

    // =============== ACTIONS ===============
    const handleCreateProformaInvoice = () => {
        if (selectedSaleOrderRows.length === 0) {
            setSnackbar({ open: true, message: 'No sale orders selected.', severity: 'warning' });
            return;
        }
        const clientId = selectedSaleOrderRows[0].PartyID;
        const selectedSOIds = selectedSaleOrderRows.map(row => row.SOId);
        // Navigate to proforma invoice stepper with selected sale order IDs
        navigate(`/main/ProformaInvoiceMaster-stepper-proforma/${clientId}`, { state: { selectedSOIds } });
    };

    const handlePdfMenuOpen = (event, invoiceId) => {
        event.stopPropagation();
        setPdfMenuAnchorEl(event.currentTarget);
        setPdfInvoiceIdForCopy(invoiceId);
        setSelectedCopyType('Original');
        setCustomCopyText('');
    };

    const handlePdfMenuClose = () => {
        setPdfMenuAnchorEl(null);
        setPdfInvoiceIdForCopy(null);
        setSelectedCopyType('Original');
        setCustomCopyText('');
    };

    const handlePrintPdf = () => {
        if (!pdfInvoiceIdForCopy) return;
        handlePdfMenuClose();
        handleProformaView(pdfInvoiceIdForCopy);
    };

    const handleProformaView = async (PImastId) => {
        if (isGeneratingPdf) return;
        setPdfOverlayVisible(true);
        setIsGeneratingPdf(true);
        setPdfLoading(true);
        setPdfError(null);

        if (!PImastId) {
            setPdfOverlayVisible(false);
            setIsGeneratingPdf(false);
            setPdfLoading(false);
            return;
        }

        const userIdSafe = userId || 'usr1';
        let formatId = 'format_1';
        let bookNameToSend = 'ProformaInvoice';
        let bookIdToSend = null;
        let selectedSeriesId = null;

        try {
            const found = proformaRows.find(i => Number(i.id) === Number(PImastId));
            if (found?.BookName) bookNameToSend = String(found.BookName).trim();
            else if (found?.bookName) bookNameToSend = String(found.bookName).trim();

            selectedSeriesId = Number(found?.BookTypeId || 0) || null;
        } catch (e) { }

        let matchedFormat = null;
        try {
            // ✅ Add cache-buster to get fresh data
            const cacheBreaker = new Date().getTime();
            const resp = await axios.get(`${API_URL}/companies`, { params: { companyId, t: cacheBreaker } });
            const comps = Array.isArray(resp?.data?.companies) ? resp.data.companies : Array.isArray(resp?.data) ? resp.data : [resp.data].filter(Boolean);
            const eq = (a, b) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();
            let matched = null;
            for (const c of comps) {
                if (Array.isArray(c.formats)) {
                    for (const f of c.formats) {
                        const fBookName = f.BookName || f.bookName;
                        const fSeriesId = Number(f.SeriesId || f.seriesId || 0);

                        if (
                            eq(fBookName, bookNameToSend) &&
                            Number(selectedSeriesId || 0) > 0 &&
                            fSeriesId === Number(selectedSeriesId)
                        ) {
                            matched = f;
                            break;
                        }
                    }

                    if (matched) break;
                }
            }
            if (matched) {
                const id = matched.BookID ?? matched.BookId ?? matched.bookId;
                if (id) { formatId = `format_${id}`; bookIdToSend = id; }
                else if (matched.FormName || matched.formName) formatId = String(matched.FormName || matched.formName);
                bookNameToSend = matched.BookName || matched.bookName || bookNameToSend;
                // ✅ Store matched format for Signature checking (fresh from API)
                matchedFormat = matched;
            }
        } catch (err) { console.warn("Format detection failed, using defaults"); }

        let imageBase64 = null;
        let signatureBase64 = null;
        try {
            const iconRes = await axios.get(`${API_URL}/company/GetIcon/${encodeURIComponent(companyId)}`);
            if (iconRes?.data?.success && iconRes?.data?.imageBase64) {
                const raw = iconRes.data.imageBase64;
                imageBase64 = typeof raw === 'string' ? raw.split('base64,').pop()?.replace(/\s/g, '') || null : null;
            }
            if (iconRes?.data?.signatureBase64) {
                signatureBase64 = iconRes.data.signatureBase64;
            }
        } catch (err) { console.warn("Failed to fetch company icon:", err); }
        // ✅ Use matched format's Signature (fresh from API, not cached context)
        if (!matchedFormat?.Signature) signatureBase64 = null;

        try {
            // Changed API endpoint to get proforma invoice data
            const proformaResp = await axios.get(`${API_URL}/get-proforma-invoice-data/${PImastId}`, { params: { companyId } });
            const { master, details, company, termsAndConditions, additionalChargeItems } = proformaResp.data || {};

            if (!master) throw new Error("No proforma invoice master data returned");

            const termsText = Array.isArray(termsAndConditions) && termsAndConditions.length > 0
                ? termsAndConditions
                    .sort((a, b) => a.SrNo - b.SrNo)
                    .map(t => `# ${t.TermsDescription}`)
                    .join('\n')
                : null;

            if (!selectedSeriesId) {
                selectedSeriesId = Number(master?.BookTypeId || 0) || null;
            }

            console.log(proformaResp);

            const items = (details || []).map(d => {
                const qty = Number(d.Qty) || 0;
                const rate = Number(d.Rate) || 0;
                const disc = Number(d.Disc) || 0;
                const amount = (qty * rate) - ((qty * rate * disc) / 100);
                return {
                    hsnCode: d.HSNNo || "",
                    ItemCode: d.itemCode,
                    description: d.description || "",
                    Remark: d.Remark || "",
                    qty, rate, Unit: d.Unit || "",
                    discount: disc,
                    cgst: Number(d.CGST || 0),
                    sgst: Number(d.SGST || 0),
                    igst: Number(d.IGST || 0),
                    TaxableAmt: Number(d.TaxableAmt) || 0,
                    amount,
                };
            });

            // Additional Charges — bound directly from Adc1Amt..Adc5Amt on
            // master; the name for each slot comes from productmaster.description
            // where TransType = 199 + slot (200-204), via additionalChargeItems.
            const additionalCharges = [1, 2, 3, 4, 5]
                .map(slot => {
                    const chargeItem = (additionalChargeItems || []).find(ci => Number(ci.TransType) === 199 + slot);
                    return {
                        name: chargeItem?.description || `Charge ${slot}`,
                        amount: Number(master[`Adc${slot}Amt`]) || 0,
                    };
                })
                .filter(c => c.amount > 0);

            const pdfData = {
                companyName: master.FirmName || "",
                clientAddress: master.clientAddress || "",
                address: master.Address || "",
                phone: master.MobileNo || "",
                gstNo: master.GSTNo || "",
                invoiceNo: master.PINumber || "",
                date: master.PIDate ? normalizeDate(master.PIDate) : "",
                transport: master.TransporterName || "SELF",
                challanNo: master.lrNumber || "",
                poNo: master.RefNo || "",
                shipToState: master.consigneeState || "",
                shipToStateCode: master.consigneeStateCode || "",
                LrNo: master.LRNo || master.LrNo || "",
                VehicleNo: master.VehicleNo || "",
                LrDate: master.LRDate ? normalizeDate(master.LRDate) : "",
                shipToGstNumber: master.consigneeGSTNo || "",
                shipToConsigneename: master.ConName || "",
                items,
                toEmail: master.EmailId || "",
                totalAmount: master.grandTotal || 0,
                mobileNo: master.MobileNo || "",
                basicAmount: Number(master.BasicAmt) || 0,
                additionalCharges,
                CGSTAmt: Number(master.CGSTAmt) || 0,
                SGSTAmt: Number(master.SGSTAmt) || 0,
                IGSTAmt: Number(master.IGSTAmt) || 0,
                RoundOff: Number(master.RoundOff) || 0,
                BillAmt: Number(master.BillAmt) || 0,
                clientState: master.clientState || "",
                clientStateCode: master.clientStateCode || "",
                imageBase64: imageBase64 || null,
                Remark: master.Remark || null,
                termsAndConditions: termsText,
                copyType: selectedCopyType || "Original",
                customCopyText: selectedCopyType === 'Other' ? customCopyText : selectedCopyType === 'Original' ? 'Original for Recipient' : selectedCopyType,
                PaymentTerm: master.PaymentTerm || null,
            };

            const companyHeader = company ? {
                CompId: company.CompId,
                CompanyName: company.CompanyName || "",
                Address: company.Address || "",
                MobileNo: company.MobileNo || "",
                EmailId: company.EmailId || "",
                Website: company.Website || "",
                GSTNo: company.GSTNo || "",
                imageBase64: imageBase64 || null,
                signatureBase64: signatureBase64 || null,
                city: company.City || null,
                BkName1: company.BkName1 || null,
                BkBranch1: company.BkBranch1 || null,
                BkAcNo1: company.BkAcNo1 || null,
                BkIFSCCode1: company.BkIFSCCode1 || null,
                // ✅ NEW: Include Color from company for PDF rendering
                headerColor: matchedFormat?.Color || '#000000',
            } : null;

            console.log(companyHeader)

            // Changed PDF endpoint to ProformaInvoice
            const pdfResp = await axios.post(
                `${PDF_API_BASE}/api/pdf/ProformaInvoice/${encodeURIComponent(PImastId)}`,
                { docId: PImastId, userId: userIdSafe, formatId, data: pdfData, company: companyHeader },
                { responseType: "blob" }
            );

            const blob = pdfResp.data;
            if (!blob || blob.size === 0) throw new Error("Empty PDF received");
            const blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));

            // (old code : commented to implement pdf view in webview)
            // const newWin = window.open(blobUrl, "_blank");
            // if (newWin) {
            //     newWin.focus();
            //     const timer = setInterval(() => {
            //         if (newWin.closed) { URL.revokeObjectURL(blobUrl); clearInterval(timer); }
            //     }, 1000);
            // } else {
            //     if (pdfUri) URL.revokeObjectURL(pdfUri);
            //     setPdfUri(blobUrl);
            //     setSnackbar({ open: true, message: "Popup blocked – showing PDF below", severity: "warning" });
            // }

            // (new code : Added to handle pdf in web and mobile both using webview)
            if (window.ReactNativeWebView) {

                const reader = new FileReader();

                reader.onloadend = () => {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: "OPEN_PDF",
                            base64: reader.result
                        })
                    );
                };

                reader.readAsDataURL(blob);

            } else {
                const newWin = window.open(blobUrl, "_blank");
                if (newWin) {
                    newWin.focus();
                    const timer = setInterval(() => {
                        if (newWin.closed) { URL.revokeObjectURL(blobUrl); clearInterval(timer); }
                    }, 1000);
                } else {
                    if (pdfUri) URL.revokeObjectURL(pdfUri);
                    setPdfUri(blobUrl);
                    setSnackbar({ open: true, message: "Popup blocked – showing PDF below", severity: "warning" });
                }
            }
        } catch (err) {
            console.error("Proforma Invoice PDF Generation Failed:", err);
            setSnackbar({ open: true, message: "Failed to generate Proforma Invoice PDF", severity: "error" });
        } finally {
            setPdfOverlayVisible(false);
            setIsGeneratingPdf(false);
            setPdfLoading(false);
        }
    };

    const handleProformaDelete = (objAryId) => {
        const arr = Array.isArray(objAryId) ? objAryId : [objAryId];
        const numericIds = arr
            .map(v => Number(v))
            .filter(n => !isNaN(n));

        if (!numericIds.length) return;

        setPendingProformaDeleteIds(numericIds);
        setOpenProformaDeleteDialog(true);
    };


    const confirmProformaDelete = async (objAryId) => {
        // const arr = Array.isArray(objAryId) ? objAryId : [objAryId];
        // const numericIds = arr.map(v => Number(v)).filter(n => !isNaN(n));
        // if (numericIds.length === 0) return;
        setOpenProformaDeleteDialog(false);
        const numericIds = pendingProformaDeleteIds;
        if (!numericIds.length) return;
        // if (!window.confirm(`Are you sure you want to delete ${numericIds.length} proforma invoice(s)?`)) return;

        try {
            const payload = { objAryId: numericIds, companyId };
            // Changed API endpoint to delete proforma invoices
            const { data } = await axios.post(`${API_URL}/delete-proforma-invoices`, payload);

            const deletedIds = Array.isArray(data.deletedIds) && data.deletedIds.length > 0 ? data.deletedIds.map(Number).filter(n => !isNaN(n)) : (data.deletedInvoices > 0 ? numericIds : []);

            const referencedIds = Array.isArray(data.referencedIds) ? data.referencedIds.map(Number).filter(n => !isNaN(n)) : [];

            if (deletedIds.length > 0) {
                setProformaRows(prev => prev.filter(row => !deletedIds.includes(Number(row.id))));
                setProformaSelectionModel(prev => prev.filter(id => !deletedIds.includes(Number(id))));
            }

            if (deletedIds.length > 0 && referencedIds.length === 0) {
                setSnackbar({ open: true, message: `Deleted Successfully.`, severity: 'success' });
            } else if (deletedIds.length > 0 && referencedIds.length > 0) {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: "warning" });
            } else if (referencedIds.length > 0) {
                setSnackbar({ open: true, message: "Cannot delete the selected item(s) because they are referenced in other records.", severity: "warning" });
            }
        } catch (err) {
            console.error('Error deleting proforma invoices:', err);
            const serverMsg = err.response?.data?.message || err.response?.data?.error;
            setSnackbar({ open: true, message: serverMsg ? `Failed: ${serverMsg}` : `Failed to delete proforma invoices`, severity: 'error' });
        } finally {
            setPendingProformaDeleteIds([]);
        }
    };

    // =============== FILTERED DATA ===============
    const filteredProformas = useMemo(() => {
        console.log(filterText)
        if (!filterText) return formatRows(proformaRows);
        const lower = filterText.toLowerCase();
        console.log("hiii ",
            proformaRows.map(r => r.companyName)
        );
        return formatRows(proformaRows.filter(row =>
            String(row.BillNumber || "").toLowerCase().includes(lower) ||
            String(row.companyName || "").toLowerCase().includes(lower)
        ));
    }, [proformaRows, filterText]);

    const filteredSaleOrders = useMemo(() => {

        if (!filterText || !expanded) return formatRows(saleOrderRows);
        const lower = filterText.toLowerCase();
        return formatRows(saleOrderRows.filter(row =>
            String(row.SONumber || "").toLowerCase().includes(lower) ||
            String(row.clientname || "").toLowerCase().includes(lower)
        ));
    }, [saleOrderRows, filterText, expanded]);

    // =============== MOBILE CARD RENDERERS ===============
    const renderProformaCards = () => {
        if (proformaLoading) return <SkeletonLoader count={3} height={120} />;
        if (filteredProformas.length === 0) return <Typography sx={{ p: 4, textAlign: 'center' }}><CustomNoDataOverlay /></Typography>;

        return filteredProformas.map((row) => {
            const isSelected = proformaSelectionModel.includes(row.id);
            const avatarColor = stringToColor(row.companyName || 'Proforma');
            const initials = getInitials(row.companyName);
            const locationText = [row.city, row.state].filter(Boolean).join(', ') || '—';

            const toggleSelection = (e) => {
                e.stopPropagation();
                setProformaSelectionModel(prev => prev.includes(row.id) ? prev.filter(i => i !== row.id) : [...prev, row.id]);
            };

            const hasReference = Boolean(row.RefNo && row.RefNo !== '—');

            return (
                <Card key={row.id} onClick={() => navigate(`/main/ProformaInvoiceMaster-edit/${row.id}`)}
                    sx={{
                        mb: 2,
                        borderLeft: `6px solid ${hasReference ? '#FF9800' : '#1976d2'}`,
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: 3,
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 6 }
                    }} >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, py: 2 }}>
                        {/* Top Row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                {role !== 'User' && (
                                    <Checkbox checked={isSelected} onChange={toggleSelection} onClick={(e) => e.stopPropagation()} size="small" />
                                )}
                                <Avatar sx={{ bgcolor: avatarColor, color: '#fff', width: 36, height: 36, flexShrink: 0 }}>
                                    {initials}
                                </Avatar>
                                <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap> {row.companyName || '—'} </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap> {locationText} </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }} noWrap> {row.PIDate} </Typography>
                        </Box>

                        {/* Middle: Bill No + Ref No */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap>  {row.BillNumber || '—'}  </Typography>
                            </Box>
                            {/* {row.RefNo && row.RefNo !== '—' && (
                                <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                                    <LinkIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
                                    <Typography variant="body2" color="text.secondary" noWrap> {row.RefNo} </Typography>
                                </Box>
                            )} */}

                            <Box sx={{ mb: 0.8 }}>
                                {hasReference ? (
                                    <Chip
                                        label={`Ref: ${row.RefNo}`}
                                        size="small"
                                        sx={{
                                            bgcolor: '#FFF3E0',
                                            color: '#E65100',
                                            fontWeight: 500
                                        }}
                                    />
                                ) : (
                                    <Chip
                                        label="Manual"
                                        size="small"
                                        sx={{ bgcolor: '#e7f5ff', color: "#0067bb" }}
                                    />
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Bottom: Contact + Phone + View */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: "wrap" }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0, flex: 1, flexWrap: "wrap" }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PersonIcon sx={{ fontSize: 18, color: '#FF9800', mr: 1, flexShrink: 0 }} />
                                    <Typography variant="body2" color="text.secondary" noWrap> {row.ContactPerson || 'N/A'} </Typography>
                                </Box>
                                {row.mobileNo && (
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <PhoneIcon sx={{ fontSize: 18, color: '#10b981', mr: 1, flexShrink: 0 }} />
                                        <Typography variant="body2" fontWeight={600} color="primary.main" noWrap> {row.mobileNo} </Typography>
                                    </Box>
                                )}
                            </Box>
                            <IconButton color="primary" onClick={(e) => { handlePdfMenuOpen(e, row.id); }} sx={{ flexShrink: 0 }} > <PrintIcon /> </IconButton>
                        </Box>
                    </CardContent>
                </Card>
            );
        });
    };

    const renderSaleOrderCards = () => {
        if (saleOrderLoading) return <SkeletonLoader count={3} height={120} />;
        if (filteredSaleOrders.length === 0) return <Typography sx={{ p: 4, textAlign: 'center' }}><CustomNoDataOverlay /></Typography>;

        return filteredSaleOrders.map((row) => {
            const checked = selectedSaleOrderIds.includes(Number(row.SOId));
            const avatarColor = stringToColor(row.clientname || 'SaleOrder');
            const initials = getInitials(row.clientname);
            const locationText = [row.city, row.state].filter(Boolean).join(', ') || '—';

            return (
                <Card key={row.SOId} sx={{ mb: 2, borderLeft: '6px solid #ff9800', borderRadius: 2, overflow: 'hidden', boxShadow: 3, bgcolor: 'background.paper', }} >
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, py: 2 }}>
                        {/* Top Row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                                <Checkbox
                                    checked={checked}
                                    onChange={(e) => {
                                        const checkedNow = e.target.checked;
                                        setSelectedSaleOrderRows(prevRows => {
                                            const prevPartyId = prevRows.length > 0 ? String(prevRows[0].PartyID ?? '') : null;
                                            if (checkedNow) {
                                                const rowPartyId = String(row.PartyID ?? '');
                                                if (prevPartyId && rowPartyId !== prevPartyId) {
                                                    setSnackbar({ open: true, message: 'Client mismatch!', severity: 'warning' });
                                                    return prevRows;
                                                }
                                                if (prevRows.some(r => r.SOId === row.SOId)) return prevRows;
                                                const newRows = [...prevRows, row];
                                                setSelectedSaleOrderIds(newRows.map(r => Number(r.SOId)));
                                                return newRows;
                                            } else {
                                                const newRows = prevRows.filter(r => Number(r.SOId) !== Number(row.SOId));
                                                setSelectedSaleOrderIds(newRows.map(r => Number(r.SOId)));
                                                return newRows;
                                            }
                                        });
                                    }}
                                    size="small"
                                />
                                <Avatar sx={{ bgcolor: avatarColor, color: '#fff', width: 36, height: 36, flexShrink: 0 }}> {initials} </Avatar>
                                <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap> {row.clientname || '—'} </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap> {locationText} </Typography>
                                </Box>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, ml: 1 }} noWrap> {row.SODate} </Typography>
                        </Box>

                        {/* Middle: SO No + Amount */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <DescriptionIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap> {row.SONumber || '—'} </Typography>
                            </Box>
                            {user?.UserCategory !== "SalesMan" && (
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>  ₹{Number(row.BillAmt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} </Typography>
                            )}
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Bottom: Contact + Mobile */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", }} >
                            {/* LEFT SIDE */}
                            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                <PersonIcon sx={{ fontSize: 18, color: "#FF9800", mr: 1, flexShrink: 0 }} />
                                <Typography variant="body2" color="text.secondary" noWrap> {row.contactPerson || "N/A"} </Typography>
                            </Box>

                            {/* RIGHT SIDE */}
                            {row.mobileNo && (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <PhoneIcon sx={{ fontSize: 18, color: "#10b981", mr: 1, flexShrink: 0 }} />
                                    <Typography variant="body2" fontWeight={600} color="primary.main" noWrap  >  {row.mobileNo} </Typography>
                                </Box>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            );
        });
    };

    const openFilterPopup = useCallback(() => {
        const { from, to } = getFinancialYearRange(new Date());
        const fromDate = filterPopupFromDate || from;
        const toDate = filterPopupToDate || to;
        if (!filterPopupFromDate) setFilterPopupFromDate(fromDate);
        if (!filterPopupToDate) setFilterPopupToDate(toDate);
        setFilterPopupSnapshot({ mode: filterPopupMode, fromDate, toDate, firms: filterPopupFirms, series: filterPopupSeries });
        setFilterPopupOpen(true);
    }, [filterPopupFromDate, filterPopupToDate, filterPopupMode, filterPopupFirms, filterPopupSeries]);

    const cancelFilterPopup = useCallback(() => {
        if (filterPopupSnapshot) {
            setFilterPopupMode(filterPopupSnapshot.mode);
            setFilterPopupFromDate(filterPopupSnapshot.fromDate);
            setFilterPopupToDate(filterPopupSnapshot.toDate);
            setFilterPopupFirms(filterPopupSnapshot.firms || []);
            setFilterPopupSeries(filterPopupSnapshot.series || []);
        }
        setFilterPopupOpen(false);
    }, [filterPopupSnapshot]);

    const handleFilterOk = useCallback(async () => {
        setExportExcelEnabled(true);
        // setFilterPopupOpen(false);  // Keep popup open

        if (!companyId) {
            setSnackbar({
                open: true,
                message: 'Company not selected',
                severity: 'warning'
            });
            return;
        }

        if (!filterPopupFromDate || !filterPopupToDate) {
            setSnackbar({
                open: true,
                message: 'Please select date range',
                severity: 'warning'
            });
            return;
        }

        setReportLoading(true);

        try {
            // ── Prepare filter params ─────────────────────────────────
            const filterParams = {
                companyId,
                fromDate: filterPopupFromDate,
                toDate: filterPopupToDate,
                mode: filterPopupMode,
                // ✅ CHANGE THIS - Send as comma-separated string or leave empty
                firms: filterPopupFirms.length > 0
                    ? filterPopupFirms.map(f => f.id).join(',')
                    : '',
                series: filterPopupSeries.length > 0
                    ? filterPopupSeries.map(s => s.id).join(',')
                    : '',
            };

            // ── Call single API ───────────────────────────────────────
            const response = await axios.get(
                `${API_URL}/proforma-invoice-register`,
                { params: filterParams }
            );

            const masterData = response?.data?.master?.data || [];
            const detailsData = response?.data?.details?.data || [];

            setMasterReportData(masterData);
            setDetailsReportData(detailsData);

            setSnackbar({
                open: true,
                message: `Report loaded: ${masterData.length} master records, ${detailsData.length} detail records`,
                severity: 'success'
            });

            console.log('Master Report Data:', masterData);
            console.log('Details Report Data:', detailsData);

        } catch (err) {
            console.error('Error fetching report data:', err);
            setSnackbar({
                open: true,
                message: err?.response?.data?.message || 'Failed to fetch report data',
                severity: 'error'
            });
        } finally {
            setReportLoading(false);
        }
    }, [
        companyId,
        filterPopupFromDate,
        filterPopupToDate,
        filterPopupMode,
        filterPopupFirms,
        filterPopupSeries,
        API_URL
    ]);

    const handleExportExcel = useCallback(() => {
        if (!masterReportData.length && !detailsReportData.length) {
            setSnackbar({ open: true, message: 'No data to export. Please apply filters first.', severity: 'warning' });
            return;
        }

        const formatDate = (val) => {
            if (!val) return '';
            const s = String(val).split('T')[0]; // strip time if present
            if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
                const [y, m, d] = s.split('-');
                return `${d}-${m}-${y}`;
            }
            return val;
        };

        const buildSheet = (data, columns) => {
            const header = columns.map(c => c.header);
            const rows = data.map(row =>
                columns.map(c => {
                    const val = row[c.key] ?? '';
                    if (c.isDate) return formatDate(val);
                    if (val === 0 || val === '0' || val === 0.0) return '';
                    // Numeric columns often arrive from the API as strings (e.g. SQL
                    // DECIMAL fields serialized as text). aoa_to_sheet writes cells
                    // based on the JS value's type, so a string here becomes a text
                    // cell that Excel's status bar (Sum/Average) will not aggregate.
                    // Coerce to a real number so these columns total correctly.
                    if (c.isNumeric) {
                        const num = Number(val);
                        return Number.isNaN(num) ? val : num;
                    }
                    return val;
                })
            );
            return XLSX.utils.aoa_to_sheet([header, ...rows]);
        };

        // Master: Categoryid & BasicAmt removed; state→State; GSTRate placed before CGSTAmt; TransactionType removed
        const masterColumns = [
            { key: 'PIDate', header: 'BillDate', isDate: true },
            { key: 'PINumber', header: 'BillNumber' },
            { key: 'FirmName', header: 'FirmName' },
            { key: 'GSTNo', header: 'GSTNo' },
            { key: 'state', header: 'State' },
            { key: 'StateCode', header: 'StateCode' },
            { key: 'GSTRate', header: 'GSTRate', isNumeric: true },
            { key: 'TaxableAmt', header: 'TaxableAmt', isNumeric: true },
            { key: 'CGSTAmt', header: 'CGSTAmt', isNumeric: true },
            { key: 'SGSTAmt', header: 'SGSTAmt', isNumeric: true },
            { key: 'IGSTAmt', header: 'IGSTAmt', isNumeric: true },
            { key: 'UTGSTAmt', header: 'UTGSTAmt', isNumeric: true },
            { key: 'GSTCEssAmt', header: 'GSTCessAmt', isNumeric: true },
            { key: 'BillAmt', header: 'BillAmt', isNumeric: true },
            { key: 'EwayBillNumber', header: 'EwayBillNumber' },
            { key: 'Remark', header: 'Remark' },
        ];

        // Details: exact sequence specified; state→State, itemCode→ItemCode, description→Description, QTY→Qty; TransactionType removed
        const detailsColumns = [
            { key: 'PIDate', header: 'BillDate', isDate: true },
            { key: 'PINumber', header: 'BillNumber' },
            { key: 'FirmName', header: 'FirmName' },
            { key: 'GSTNo', header: 'GSTNo' },
            { key: 'state', header: 'State' },
            { key: 'StateCode', header: 'StateCode' },
            { key: 'itemCode', header: 'ItemCode' },
            { key: 'description', header: 'Description' },
            { key: 'HSNNo', header: 'HSNNo' },
            { key: 'Unit', header: 'Unit' },
            { key: 'QTY', header: 'Qty', isNumeric: true },
            { key: 'Rate', header: 'Rate', isNumeric: true },
            { key: 'Disc', header: 'Disc', isNumeric: true },
            { key: 'TaxableAmt', header: 'TaxableAmt', isNumeric: true },
            { key: 'GSTRate', header: 'GSTRate', isNumeric: true },
            { key: 'CGSTRate', header: 'CGSTRate', isNumeric: true },
            { key: 'SGSTRate', header: 'SGSTRate', isNumeric: true },
            { key: 'IGSTRate', header: 'IGSTRate', isNumeric: true },
            { key: 'CESSRate', header: 'CESSRate', isNumeric: true },
            { key: 'UTGSTRate', header: 'UTGSTRate', isNumeric: true },
            { key: 'CGSTAmt', header: 'CGSTAmt', isNumeric: true },
            { key: 'SGSTAmt', header: 'SGSTAmt', isNumeric: true },
            { key: 'IGSTAmt', header: 'IGSTAmt', isNumeric: true },
            { key: 'CESSAmt', header: 'CESSAmt', isNumeric: true },
            { key: 'UTGSTAmt', header: 'UTGSTAmt', isNumeric: true },
            { key: 'ItemRemark', header: 'ItemRemark' },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, buildSheet(masterReportData, masterColumns), 'Master');
        XLSX.utils.book_append_sheet(wb, buildSheet(detailsReportData, detailsColumns), 'Details');

        const fromDate = filterPopupFromDate || 'start';
        const toDate = filterPopupToDate || 'end';
        XLSX.writeFile(wb, `ProformaInvoiceRegister_${fromDate}_${toDate}.xlsx`);

        setSnackbar({ open: true, message: 'Excel exported successfully!', severity: 'success' });
    }, [masterReportData, detailsReportData, filterPopupFromDate, filterPopupToDate]);

    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!API_URL || !companyId) return;
        let active = true;
        const fetchAllFirms = async () => {
            try {
                setFirmLoading(true);
                const res = await axios.get(`${API_URL}/company-search`, { params: { field: 'FirmName', search: '', companyId, limit: 1000 } });
                const data = Array.isArray(res?.data?.data) ? res.data.data : [];
                if (!active) return;
                const opts = data.map(r => ({ id: r?.id, label: r?.FirmName || '' })).filter(o => o.label);
                setFirmAllOptions(opts);
                if (!(firmInputValue || '').trim()) setFirmOptions(opts);
            } catch (err) {
                if (!active) return;
                setFirmAllOptions([]);
                setFirmOptions([]);
            } finally {
                if (active) setFirmLoading(false);
            }
        };
        fetchAllFirms();
        return () => { active = false; };
    }, [companyId, API_URL, filterPopupOpen]);

    useEffect(() => {
        if (!(firmInputValue || '').trim()) {
            setFirmOptions(firmAllOptions);
            return;
        }
        const q = firmInputValue.toLowerCase();
        setFirmOptions(firmAllOptions.filter(o => o.label.toLowerCase().includes(q)));
    }, [firmInputValue, firmAllOptions]);

    useEffect(() => {
        if (!filterPopupOpen) return;
        if (!companyId) return;
        let active = true;
        const fetchSeries = async () => {
            try {
                setSeriesLoading(true);
                const { data } = await axios.get(`${API_URL}/bookmaster-series`, {
                    params: { companyId, bookTypeName: 'Proforma Invoice' }
                });
                if (!active) return;
                setSeriesOptions(Array.isArray(data) ? data.map(item => ({ id: item?.BookMastID, label: item?.SeriesName })) : []);
            } catch (err) {
                if (active) setSeriesOptions([]);
            } finally {
                if (active) setSeriesLoading(false);
            }
        };
        fetchSeries();
        return () => { active = false; };
    }, [companyId, API_URL, filterPopupOpen]);

    return (
        <Box sx={{ pt: 0.7, px: { xs: 2, sm: 3, md: 4 } }}>
            {/* Header */}


            {/* ── Module Header ──────────────────────────────────────────────
                If sale order rows are selected, the Add button creates a
                Proforma Invoice from those orders instead of opening blank.
            ────────────────────────────────────────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)', borderRadius: '6px 6px 0 0' }} />
                <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderTop: 0, borderRadius: '0 0 8px 8px', px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700} color="primary.main" sx={{ fontSize: '1.05rem' }}>
                        Proforma Invoices
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" size="small" startIcon={<FilterAltIcon />} onClick={openFilterPopup} sx={{ textTransform: 'none' }}>Filter</Button>
                        {/* <Button variant="outlined" size="small" onClick={handleCardClick} sx={{ textTransform: 'none' }}>
                            {expanded ? 'Hide Orders' : 'Show Orders'}
                        </Button> */}
                        {/* If sale order rows are selected → create PI from orders; else open blank form */}
                        <Button variant="contained" size="small" startIcon={<AddIcon />}
                            onClick={() => {
                                if (selectedSaleOrderRows.length > 0) {
                                    handleCreateProformaInvoice();
                                } else {
                                    navigate("/main/ProformaInvoiceMaster-stepper");
                                }
                            }}
                        >
                            {isSmallDevice ? 'Add' : 'Add Proforma Invoice'}
                        </Button>
                    </Box>
                </Paper>
            </Box>


            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <Box sx={{ p: { xs: 0.5, md: 0.5 } }}>
                    {/* Mobile Search */}
                    {!isDesktop && (
                        <Box sx={{ mb: 2, px: 1 }}>
                            <TextField fullWidth size="small" placeholder={expanded ? "Search Sale Order..." : "Search Proforma Invoice..."} value={filterText} onChange={(e) => setFilterText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>, }} variant="outlined" />
                        </Box>
                    )}

                    {/* Mobile Bulk Delete Bar (Proforma Invoices) */}
                    {!isDesktop && role !== 'User' && proformaSelectionModel.length > 0 && !expanded && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, mb: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
                            <Box display="flex" alignItems="center">
                                <Checkbox
                                    checked={
                                        filteredProformas.length > 0 &&
                                        filteredProformas.every(r =>
                                            proformaSelectionModel.includes(r.id)
                                        )
                                    }
                                    onChange={() => {
                                        const allSelected =
                                            filteredProformas.length > 0 &&
                                            filteredProformas.every(r =>
                                                proformaSelectionModel.includes(r.id)
                                            );

                                        if (allSelected) {
                                            setProformaSelectionModel([]);
                                        } else {
                                            setProformaSelectionModel(
                                                filteredProformas.map(r => r.id)
                                            );
                                        }
                                    }}
                                />
                                <Typography variant="body2">{proformaSelectionModel.length} selected</Typography>
                            </Box>

                            <Tooltip title="Delete selected">
                                <IconButton onClick={() => handleProformaDelete(proformaSelectionModel)} color="error"> <DeleteIcon /> </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    {/* Sale Order Section (Pending Proforma) */}
                    {expanded && (
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            {isDesktop ? (
                                <Paper elevation={2} sx={{ borderRadius: 3, p: 1.5, overflow: 'hidden', mb: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="h6" fontWeight={600}>Sale Order List</Typography>
                                    </Box>
                                    <Box sx={{ height: '71vh', width: '100%', position: 'relative' }}>
                                        <LoadingOverlay isVisible={saleOrderLoading} />
                                        {!saleOrderLoading && filteredSaleOrders.length === 0 && <NoDataComponent />}
                                        {!saleOrderLoading && filteredSaleOrders.length > 0 && (
                                            <DataGrid rows={filteredSaleOrders} columns={saleOrderColumns} getRowHeight={() => 'auto'} getRowId={(row) => row.SOId} pageSizeOptions={[50, 100]}
                                                sx={{
                                                    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                                                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                                    '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },

                                                    // Background styling
                                                    '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                                                    '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },

                                                    // Row styling
                                                    '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                                    '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                                                    '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },

                                                    // Selected row styling (with !important to override default MUI)
                                                    '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },

                                                    // Cell content alignment
                                                    '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                                }}
                                                slots={{
                                                    noResultsOverlay: CustomNoDataOverlay
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Paper>
                            ) : (
                                <Paper sx={{ borderRadius: 3, p: 1.5, overflow: 'hidden' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                        <Typography variant="h6" fontWeight={600}>Sale Order List</Typography>
                                    </Box>
                                    {saleOrderRows?.length === 0 ? <NoDataComponent /> : renderSaleOrderCards()}
                                </Paper>
                            )}

                        </Collapse>
                    )}

                    {/* Proforma Invoice List */}
                    <Box>
                        {isDesktop ? (
                            <Paper elevation={2} sx={{ borderRadius: 3, p: 0.5, overflow: 'hidden' }}>
                                <Box sx={{ height: '71vh', width: '100%', position: 'relative' }}>
                                    <LoadingOverlay isVisible={proformaLoading} />
                                    {!proformaLoading && proformaRows.length === 0 && <NoDataComponent />}
                                    {!proformaLoading && proformaRows.length > 0 && <DataGrid rows={proformaRows} getRowId={(row) => row.id} getRowHeight={() => "auto"} pageSizeOptions={[50, 100]} disableRowSelectionOnClick columns={proformaColumns}
                                        sx={{
                                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                                            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                            '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold', },

                                            // Background styling
                                            '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                                            '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },

                                            // Row styling
                                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                                            '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },

                                            // Selected row styling (with !important to override default MUI)
                                            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },

                                            // Cell content alignment
                                            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                                        }}
                                        slots={{
                                            noResultsOverlay: CustomNoDataOverlay
                                        }}
                                    />}

                                </Box>
                            </Paper>
                        ) : (
                            <Paper sx={{ borderRadius: 3, p: 1.5, overflow: 'hidden', display: !expanded ? 'block' : 'none' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography variant="h6" fontWeight={600}>Proforma Invoice List</Typography>
                                </Box>
                                {proformaLoading && <SkeletonLoader count={2} height={120} />}
                                {!proformaLoading && proformaRows.length === 0 && <NoDataComponent />}
                                {!proformaLoading && proformaRows.length > 0 && renderProformaCards()}
                            </Paper>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* Overlays & Snackbar */}
            {pdfLoading && (
                <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, justifyContent: 'center' }}>
                    <CircularProgress size={18} />
                    <Typography>Preparing PDF...</Typography>
                </Box>
            )}

            {pdfOverlayVisible && (
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center', bgcolor: '#111', p: '40px 60px', borderRadius: '20px' }}>
                        <CircularProgress size={60} thickness={4} sx={{ color: '#fff' }} />
                        <Typography variant="h6" sx={{ color: '#fff', mt: 3 }}>Generating Proforma Invoice PDF…</Typography>
                        <Typography variant="body2" sx={{ color: '#aaa', mt: 1 }}>Please wait</Typography>
                    </Box>
                </Box>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant='filled'>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* ── Delete Confirmation Dialog ──────────────────────────── */}
            <Dialog open={openProformaDeleteDialog} onClose={() => setOpenProformaDeleteDialog(false)} maxWidth="xs" fullWidth>
                <Box sx={{ height: 4, background: 'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)' }} />
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>Confirm Deletion</Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to delete{' '}
                        <Typography component="span" variant="body2" fontWeight={700} color="error.main">{pendingProformaDeleteIds.length}</Typography>
                        {' '}proforma invoice(s)? This action cannot be undone.
                    </Typography>
                </Box>
                <Divider />
                <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => setOpenProformaDeleteDialog(false)} variant="outlined" color="inherit" size="small">Cancel</Button>
                    <Button color="error" variant="contained" size="small" startIcon={<DeleteIcon />} onClick={confirmProformaDelete}>Delete</Button>
                </Box>
            </Dialog>

            {/* Filter Dialog */}
            <Dialog
                open={filterPopupOpen}
                onClose={cancelFilterPopup}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } }}
            >
                {/* Header */}
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pb: 1.5,
                        pt: 2,
                        px: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                borderRadius: '50%',
                                p: 0.8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <FilterListIcon sx={{ color: 'white', fontSize: 20 }} />
                        </Box>
                        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                            Filter Invoices
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={cancelFilterPopup}
                        size="small"
                        sx={{
                            color: 'white',
                            bgcolor: 'rgba(255,255,255,0.15)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ px: 3, py: 2, bgcolor: '#fafafa' }}>
                    {/* Filter Mode Selection */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            mb: 1,
                            mt: 1,
                            bgcolor: 'white',
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1 }} />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ color: 'text.primary' }}>
                                Filter Mode
                            </Typography>
                        </Box>
                        <RadioGroup
                            row
                            value={filterPopupMode}
                            onChange={(e) => setFilterPopupMode(e.target.value)}
                            sx={{ display: 'flex', gap: 2 }}
                        >
                            <FormControlLabel
                                value="datewise"
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            '&.Mui-checked': { color: 'primary.main' }
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            📅 Datewise
                                        </Typography>
                                    </Box>
                                }
                                sx={{
                                    flex: 1,
                                    m: 0,
                                    bgcolor: filterPopupMode === 'datewise' ? '#e3f2fd' : '#f5f5f5',
                                    borderRadius: 1.5,
                                    px: 2,
                                    py: 0.5,
                                    border: filterPopupMode === 'datewise' ? '2px solid' : '2px solid transparent',
                                    borderColor: 'primary.main',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        bgcolor: filterPopupMode === 'datewise' ? '#e3f2fd' : '#eeeeee',
                                    }
                                }}
                            />
                            <FormControlLabel
                                value="partywise"
                                control={
                                    <Radio
                                        size="small"
                                        sx={{
                                            '&.Mui-checked': { color: 'primary.main' }
                                        }}
                                    />
                                }
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2" fontWeight={500}>
                                            👥 Partywise
                                        </Typography>
                                    </Box>
                                }
                                sx={{
                                    flex: 1,
                                    m: 0,
                                    bgcolor: filterPopupMode === 'partywise' ? '#e3f2fd' : '#f5f5f5',
                                    borderRadius: 1.5,
                                    px: 2,
                                    py: 0.5,
                                    border: filterPopupMode === 'partywise' ? '2px solid' : '2px solid transparent',
                                    borderColor: 'primary.main',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        bgcolor: filterPopupMode === 'partywise' ? '#e3f2fd' : '#eeeeee',
                                    }
                                }}
                            />
                        </RadioGroup>
                    </Paper>

                    {/* Filter Fields */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Date Range */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: 'white',
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#ff6b6b', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    📆 Date Range
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}
                                    >
                                        From Date
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        value={filterPopupFromDate}
                                        onChange={(e) => setFilterPopupFromDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa',
                                                borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="caption"
                                        fontWeight={600}
                                        sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}
                                    >
                                        To Date
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        value={filterPopupToDate}
                                        onChange={(e) => setFilterPopupToDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa',
                                                borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Paper>

                        {/* FirmName */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: 'white',
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#4ecdc4', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    🏢 FirmName
                                </Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={firmOptions}
                                loading={firmLoading}
                                value={filterPopupFirms}
                                onChange={(_, newValue) => setFilterPopupFirms(newValue)}
                                inputValue={firmInputValue}
                                onInputChange={(_, v) => setFirmInputValue(v)}
                                getOptionLabel={(o) => o?.label || ''}
                                isOptionEqualToValue={(o, v) => String(o?.id ?? o?.label) === String(v?.id ?? v?.label)}
                                renderTags={() => null}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            checked={selected}
                                            size="small"
                                            sx={{
                                                mr: 1,
                                                color: '#4ecdc4',
                                                '&.Mui-checked': { color: '#4ecdc4' }
                                            }}
                                        />
                                        <Typography variant="body2">{option?.label || ''}</Typography>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        placeholder={filterPopupFirms?.length ? `${filterPopupFirms.length} firm(s) selected` : 'Select firms (ALL)'}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa',
                                                borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    {filterPopupFirms?.length > 0 && (
                                                        <Chip
                                                            label={`${filterPopupFirms.length}`}
                                                            size="small"
                                                            sx={{
                                                                ml: 0.5,
                                                                height: 22,
                                                                bgcolor: '#4ecdc4',
                                                                color: 'white',
                                                                fontWeight: 700,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    )}
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                ListboxProps={{ style: { maxHeight: 200 } }}
                            />
                        </Paper>

                        {/* Series */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: 'white',
                                border: '1px solid #e0e0e0',
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box sx={{ width: 4, height: 20, bgcolor: '#a55eea', borderRadius: 1 }} />
                                <Typography variant="subtitle2" fontWeight={600} sx={{ color: 'text.primary' }}>
                                    📋 Series
                                </Typography>
                            </Box>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                options={seriesOptions}
                                loading={seriesLoading}
                                value={filterPopupSeries}
                                onChange={(_, newValue) => setFilterPopupSeries(newValue)}
                                getOptionLabel={(o) => o?.label || ''}
                                isOptionEqualToValue={(o, v) => String(o?.id ?? o?.label) === String(v?.id ?? v?.label)}
                                renderTags={() => null}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            checked={selected}
                                            size="small"
                                            sx={{
                                                mr: 1,
                                                color: '#a55eea',
                                                '&.Mui-checked': { color: '#a55eea' }
                                            }}
                                        />
                                        <Typography variant="body2">{option?.label || ''}</Typography>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        size="small"
                                        placeholder={filterPopupSeries?.length ? `${filterPopupSeries.length} series selected` : 'Select series (ALL)'}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                bgcolor: '#f8f9fa',
                                                borderRadius: 1.5,
                                                '&:hover': { bgcolor: '#e9ecef' },
                                                '&.Mui-focused': { bgcolor: 'white' }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#dee2e6' }
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    {filterPopupSeries?.length > 0 && (
                                                        <Chip
                                                            label={`${filterPopupSeries.length}`}
                                                            size="small"
                                                            sx={{
                                                                ml: 0.5,
                                                                height: 22,
                                                                bgcolor: '#a55eea',
                                                                color: 'white',
                                                                fontWeight: 700,
                                                                fontSize: '0.75rem'
                                                            }}
                                                        />
                                                    )}
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                ListboxProps={{ style: { maxHeight: 200 } }}
                            />
                        </Paper>
                    </Box>
                </DialogContent>

                {/* Action Buttons */}
                <DialogActions
                    sx={{
                        px: 3,
                        py: 1.5,
                        bgcolor: '#fafafa',
                        borderTop: '1px solid #e0e0e0',
                        gap: 2
                    }}
                >
                    <Button
                        onClick={cancelFilterPopup}
                        variant="outlined"
                        sx={{
                            flex: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                            borderColor: '#d0d0d0',
                            color: 'text.secondary',
                            '&:hover': { borderColor: '#999', bgcolor: '#f5f5f5' }
                        }}
                    >
                        ✕ Cancel
                    </Button>

                    {/* Export Excel Button */}
                    <Button
                        onClick={handleExportExcel}
                        variant="outlined"
                        disabled={!exportExcelEnabled}
                        sx={{
                            flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1.2,
                            borderColor: !exportExcelEnabled ? '#d0d0d0' : '#2e7d32',
                            color: !exportExcelEnabled ? 'text.disabled' : '#2e7d32',
                            '&:hover': {
                                borderColor: '#2e7d32',
                                bgcolor: '#f1f8e9'
                            },
                            '&.Mui-disabled': {
                                borderColor: '#d0d0d0',
                                color: 'text.disabled'
                            }
                        }}
                    >
                        📊 Export Excel
                    </Button>

                    <Button
                        onClick={handleFilterOk}
                        variant="contained"
                        disabled={reportLoading}
                        startIcon={reportLoading ? <CircularProgress size={16} /> : null}
                        sx={{
                            flex: 1,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)',
                                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)'
                            }
                        }}
                    >
                        ✓ {reportLoading ? 'Loading...' : 'Apply Filters'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Popover for PDF Copy Selection */}
            <Popover
                open={Boolean(pdfMenuAnchorEl)}
                anchorEl={pdfMenuAnchorEl}
                onClose={handlePdfMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 280, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' } }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.primary' }}>
                        Select Copy Type
                    </Typography>
                    <RadioGroup value={selectedCopyType} onChange={(e) => setSelectedCopyType(e.target.value)}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormControlLabel
                                value="Original"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography variant="body2">Original</Typography>}
                                sx={{ m: 0, p: 1, bgcolor: selectedCopyType === 'Original' ? '#e3f2fd' : 'transparent', borderRadius: 1, transition: 'all 0.2s ease' }}
                            />
                            <FormControlLabel
                                value="Duplicate copy"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography variant="body2">Duplicate copy</Typography>}
                                sx={{ m: 0, p: 1, bgcolor: selectedCopyType === 'Duplicate copy' ? '#e3f2fd' : 'transparent', borderRadius: 1, transition: 'all 0.2s ease' }}
                            />
                            <FormControlLabel
                                value="Triplicate copy"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography variant="body2">Triplicate copy</Typography>}
                                sx={{ m: 0, p: 1, bgcolor: selectedCopyType === 'Triplicate copy' ? '#e3f2fd' : 'transparent', borderRadius: 1, transition: 'all 0.2s ease' }}
                            />
                            <FormControlLabel
                                value="Transport copy"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography variant="body2">Transport copy</Typography>}
                                sx={{ m: 0, p: 1, bgcolor: selectedCopyType === 'Transport copy' ? '#e3f2fd' : 'transparent', borderRadius: 1, transition: 'all 0.2s ease' }}
                            />
                            <FormControlLabel
                                value="Other"
                                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'primary.main' } }} />}
                                label={<Typography variant="body2">Other</Typography>}
                                sx={{ m: 0, p: 1, bgcolor: selectedCopyType === 'Other' ? '#e3f2fd' : 'transparent', borderRadius: 1, transition: 'all 0.2s ease' }}
                            />
                            {selectedCopyType === 'Other' && (
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Enter custom type"
                                    value={customCopyText}
                                    onChange={(e) => {
                                        if (e.target.value.length <= 15) {
                                            setCustomCopyText(e.target.value);
                                        }
                                    }}
                                    inputProps={{ maxLength: 15 }}
                                    helperText={`${customCopyText.length}/15`}
                                    sx={{ mt: 1, mb: 1 }}
                                />
                            )}
                        </Box>
                    </RadioGroup>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            onClick={handlePdfMenuClose}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handlePrintPdf}
                            variant="contained"
                            size="small"
                            fullWidth
                            startIcon={<PrintIcon sx={{ fontSize: 18 }} />}
                            sx={{ textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                        >
                            Open
                        </Button>
                    </Box>
                </Box>
            </Popover>

        </Box>
    );
}